
import { Vue, Component, Watch, Ref } from "vue-property-decorator";
import draggable from "vuedraggable";
import { formatDate, formatBool } from "../../utils";
import { OrderModule, SystemModule, TruckModule } from "@/store/modules";
import { getModule } from "vuex-module-decorators";
import ContextMenu from "@/components/widgets/ContextMenu.vue";
import RightClickMenu from "@/components/widgets/RightClickMenu.vue";
import {
  GetOrdersRequest,
  OrderListItemResource,
} from "../../models/api/orders/get";
import notifications from "../../lib/notifications";
import {
  EditNonCdsOrderViewModel,
  NonCdsOrderCommodityViewModel,
} from "@/models/ui/nonCdsOrders";
import { CreateNonCdsOrderRequest } from "../../models/api/nonCdsOrders/create";
import { UpdateNonCdsOrderRequest } from "../../models/api/nonCdsOrders/update";
import { TruckOrderFreightDetailResource } from "../../models/api/trucks/orderFreight";
import permissions from "@/security/permissions";

const truckModule = getModule(TruckModule);
const orderModule = getModule(OrderModule);
const systemModule = getModule(SystemModule);

@Component({
  components: {
    ContextMenu,
    draggable,
    RightClickMenu,
  },
})
export default class TruckOrderList extends Vue {
  cdsRightClickLocation: Object = { x: 0, y: 0 };
  nonCdsRightClickLocation: Object = { x: 0, y: 0 };
  addOrdersModalVisible: boolean = false;
  localTruckOrders: any = null;
  orderResults: OrderListItemResource[] = null;
  fieldsToUse: string[] = [
    "headerNumber",
    "crn",
    "received",
    "billTo.displayId",
    "scheduledDeliveryDate",
    "consignee.address.city",
    "consignee.address.zip",
    "isAsapDelivery",
  ];
  loadingOrdersForModal: boolean = false;
  orderNumberFilter: number = null;
  crnFilter: string = null;
  zipsFilter: string[] = null;
  nameFilter: string = null;
  deliveryTypeFilter: string = null;
  addressFilter: string = null;
  manufacturerFilter: string = null;
  billToFilter: string = null;
  phoneNumberFilter: string = null;
  validZipsOnlyFilter: boolean = true;
  freightDetailsModalVisible: boolean = false;
  freightDetailsOrderId: number = null;
  loadingFreightDetails: boolean = false;
  loadingCommodities: boolean = false;
  freightItems: TruckOrderFreightDetailResource[] = null;
  editNonCdsOrderViewModel: EditNonCdsOrderViewModel = null;
  selectedItem: any = null;
  lastGetOrdersRequest: GetOrdersRequest = null;

  async created() {
    this.localTruckOrders = this.currentTruckOrders;
    await this.loadTruckCommodities();
  }

  @Watch("currentTruck")
  truckChanged() {
    this.localTruckOrders = this.currentTruckOrders;
    this.loadTruckCommodities();
  }

  menuItems: any[] = [
    {
      title: "Open Order",
      name: "open",
      action: "openOrder",
      itemAction: this.openOrder,
      cdsOrders: true,
      nonCdsOrders: false,
      hasPermission: true,
    },
    {
      title: "Preview Order",
      name: "preview",
      action: "previewOrder",
      itemAction: this.previewOrder,
      cdsOrders: true,
      nonCdsOrders: false,
      hasPermission: true,
    },
    {
      title: "Edit Order",
      name: "edit",
      action: "editNonCdsOrder",
      itemAction: this.editNonCdsOrder,
      cdsOrders: false,
      nonCdsOrders: true,
      hasPermission: true,
    },
    {
      title: "Freight Details",
      name: "freight-details",
      action: "showFreightDetailsModal",
      itemAction: this.showFreightDetailsModal,
      cdsOrders: true,
      nonCdsOrders: true,
      hasPermission: this.hasPermission(permissions.order.view.commodity),
    },
    {
      title: "Remove from Truck",
      name: "remove-from-truck",
      action: "removeOrderFromTruck",
      itemAction: this.removeOrderFromTruck,
      cdsOrders: true,
      nonCdsOrders: true,
      hasPermission: this.hasPermission(permissions.truck.addOrder),
    },
  ];

  get cdsMenuItems() {
    return this.menuItems.filter((x) => x.cdsOrders && x.hasPermission);
  }

  get nonCdsMenuItems() {
    return this.menuItems.filter((x) => x.nonCdsOrders && x.hasPermission);
  }

  onRowClick(item) {
    this.clearRightClick();
    if (this.selectedItem == item) {
      this.selectedItem = null;
    } else {
      this.selectedItem = item;
    }
  }

  onRightClicked(item, event) {
    event.preventDefault();

    if (this.selectedItem && this.selectedItem.orderHeaderNumber != null && !this.selectedItem.orderNotFound) {
      this.cdsRightClickLocation = { x: event.x, y: event.y };
      this.nonCdsRightClickLocation = null;
    }

    if (this.selectedItem && this.selectedItem.nonCDSOrderId != null) {
      this.cdsRightClickLocation = null;
      this.nonCdsRightClickLocation = { x: event.x, y: event.y };
    }

    if (this.selectedItem != item) {
      this.selectedItem = item;
    }
  }

  clickMenu(menuItem) {
    menuItem.itemAction(this.selectedItem);
    this.clearRightClick();
  }

  clearRightClick() {
    this.cdsRightClickLocation = null;
    this.nonCdsRightClickLocation = null;
  }

  truckCommodities: any[] = [];

  async loadTruckCommodities() {
    this.loadingCommodities = true;
    try {
      this.truckCommodities = await truckModule.getTruckCommodities(
        this.currentTruck.id
      );
    } catch (ex) {
      notifications.error(
        "Error Loading Commodities",
        "Failed to load truck commodities.",
        ex
      );
    } finally {
      this.loadingCommodities = false;
    }
  }

  async onDrop(e) {
    let detailSequences = [];
    if (this.currentTruckOrders.length > e.oldIndex) {
      let movedDetailId = this.currentTruckOrders[e.oldIndex].id;

      for (let i = 0; i < this.currentTruckOrders.length; i++) {
        if (i == e.oldIndex)
          detailSequences.push({
            truckDetailId: this.currentTruckOrders[i].id,
            sequence: e.newIndex,
          });
        else {
          if (e.newIndex > e.oldIndex) {
            if (i > e.oldIndex && i <= e.newIndex)
              detailSequences.push({
                truckDetailId: this.currentTruckOrders[i].id,
                sequence: i - 1,
              });
            else
              detailSequences.push({
                truckDetailId: this.currentTruckOrders[i].id,
                sequence: i,
              });
          } else {
            if (i < e.oldIndex && i >= e.newIndex)
              detailSequences.push({
                truckDetailId: this.currentTruckOrders[i].id,
                sequence: i + 1,
              });
            else
              detailSequences.push({
                truckDetailId: this.currentTruckOrders[i].id,
                sequence: i,
              });
          }
        }
      }

      let request = {
        truckId: this.currentTruck.id,
        detailSequences: detailSequences,
      };

      await truckModule
        .updateTruckDetailSequence(request)
        .then(() => {
          notifications.success(
            "Order Resequenced",
            "Order successfully sequenced on truck."
          );
        })
        .catch((ex) => {
          notifications.error(
            "Error Resequencing Orders",
            "Failed to resequence orders",
            ex
          );
        });

      truckModule.loadTruck(this.currentTruck.id);
    }
  }

  openOrder(item) {
    if(!item.orderNotFound){
      this.$router.push({ path: `/order/${item.orderHeaderNumber}` });
    }
  }

  previewOrder(item) {
    if(!item.orderNotFound){
      orderModule.orderPreviewShortcutAction({
        selectedItems: [{ number: item.orderHeaderNumber }],
      });
    }
  }

  showFreightDetailsModal(item) {
    if(!item.orderNotFound){
      this.freightDetailsOrderId = item.id;
      this.freightDetailsModalVisible = true;
    }
  }

  async editNonCdsOrder(item) {
    await this.showEditNonCdsOrderModal(item.nonCDSOrderId);
  }

  async showEditNonCdsOrderModal(orderId = null) {
    this.editNonCdsOrderViewModel = new EditNonCdsOrderViewModel();
    this.editNonCdsOrderViewModel.modalIsVisible = true;

    if (orderId) {
      try {
        const existingOrder = await orderModule.getNonCdsOrder(orderId);
        const commodities = await orderModule.getNonCdsOrderCommodities(
          orderId
        );

        this.editNonCdsOrderViewModel.displayExistingOrder(
          existingOrder,
          commodities
        );
      } catch (ex) {
        notifications.error("Error Loading Order", "Failed to load order", ex);
        this.editNonCdsOrderViewModel.modalIsVisible = false;
        return;
      }
    } else {
      this.editNonCdsOrderViewModel.displayNewOrder();
    }
  }

  removeOrderFromTruck(item) {
    this.$bvModal
      .msgBoxConfirm(
        `Are you sure you wish to remove order ${item.id} from truck ${this.currentTruck.name}? This action cannot be undone.`
      )
      .then((value) => {
        if (value) {
          truckModule
            .removeOrderFromTruck({
              truckId: this.currentTruck.id,
              orderId: item.id,
            })
            .then(() => {
              notifications.success(
                "Order Removed",
                "Order successfully removed from truck."
              );
              truckModule.loadTruck(this.currentTruck.id);
            })
            .catch((ex) => {
              notifications.error(
                "Order Not Removed",
                "Failed to remove order from truck",
                ex
              );
            });
        }
      })
      .catch((err) => {
        // An error occurred
      });
  }

  selectedOrderNumber: string = null;

  $refs: {
    contextMenu: ContextMenu;
  };

  get currentTruck() {
    return truckModule.currentTruck;
  }

  get currentTruckOrders() {
    if (!this.currentTruck) return [];

    return truckModule.currentTruck.orders;
  }

  getFormattedDate(dateToFormat) {
    return formatDate(dateToFormat);
  }

  getFormattedBool(boolToFormat) {
    return formatBool(boolToFormat);
  }

  orderContextMenuHidden() {
    this.selectedOrderNumber = null;
  }

  showAddOrdersModal() {
    this.addOrdersModalVisible = true;
  }

  async showAddOrdersToTruckModal() {
    await this.loadOrders();
  }

  async loadFreightDetails() {
    this.loadingFreightDetails = true;
    this.freightItems = await truckModule.getTruckOrderFreightDetails({
      truckId: this.currentTruck.id,
      orderId: this.freightDetailsOrderId,
    });
    this.loadingFreightDetails = false;
  }

  async loadOrders() {
    this.orderResults = null;
    const request: GetOrdersRequest = new GetOrdersRequest();
    request.pageNumber = 1;
    request.pageSize = 100;
    request.facilityCodes = this.currentTruck.validFacilityCodes;
    request.deliveryDate = this.currentTruck.deliveryDate;
    request.zipZip3s = this.zipsFilter;
    request.orderNumber = +this.orderNumberFilter;
    request.crn = this.crnFilter;
    request.consigneeName = this.nameFilter;
    request.consigneeAddress = this.addressFilter;
    request.consigneePhone = this.phoneNumberFilter;
    request.excludeWillCall = true;

    if (this.billToFilter) request.billTos = [this.billToFilter];

    if (this.manufacturerFilter)
      request.manufacturers = [this.manufacturerFilter];

    if (this.deliveryTypeFilter)
      request.deliveryTypes = [this.deliveryTypeFilter];

    if (this.validZipsOnlyFilter)
      request.useTruckZipsTruckId = this.currentTruck.id;
    else
      request.useTruckZipsTruckId = null;

    request.hideManifestedOrders = true;

    try {
      this.loadingOrdersForModal = true;
      const response = await orderModule.getOrders(request);
      this.orderResults = response.items;
      this.lastGetOrdersRequest = request;
    } catch (ex) {
      notifications.error("Orders", "Failed to load orders", ex);
    }

    this.loadingOrdersForModal = false;
  }

  async loadMoreOrders() {
    if (this.lastGetOrdersRequest) {
      this.lastGetOrdersRequest.pageNumber += 1;
      try {
        this.loadingOrdersForModal = true;
        const response = await orderModule.getOrders(this.lastGetOrdersRequest);
        if (this.orderResults)
          this.orderResults = this.orderResults.concat(response.items);
        else this.orderResults = response.items;
      } catch (ex) {
        notifications.error("Orders", "Failed to load orders", ex);
      }
      this.loadingOrdersForModal = false;
    }
  }

  async addOrderToTruck(row: OrderListItemResource) {
    try {
      await truckModule.addOrderToTruck({
        truckId: this.currentTruck.id,
        orderNumber: +row.number,
      });
      notifications.success(
        "Add Order to Truck",
        `Order ${row.number} added to truck.`
      );
    } catch (ex) {
      notifications.error(
        "Add Order to Truck",
        `Failed to add order ${row.number} to truck`,
        ex
      );
      throw ex;
    }
  }

  async reset() {
    this.orderResults = [];
    this.loadingOrdersForModal = false;
    this.lastGetOrdersRequest = null;
    this.resetFilters();
    await truckModule.loadTruck(this.currentTruck.id);
  }

  resetFilters() {
    this.addressFilter = null;
    this.manufacturerFilter = null;
    this.billToFilter = null;
    this.phoneNumberFilter = null;
    this.orderNumberFilter = null;
    this.crnFilter = null;
    this.zipsFilter = null;
    this.nameFilter = null;
    this.deliveryTypeFilter = null;
    this.validZipsOnlyFilter = true;
  }

  resetFreightDetails() {
    this.freightItems = null;
    this.loadingFreightDetails = false;
    this.freightDetailsOrderId = null;
  }

  closeEditNonCdsOrderModal() {
    this.editNonCdsOrderViewModel.modalIsVisible = false;
  }

  async saveNonCdsOrder() {
    this.editNonCdsOrderViewModel.saving = true;
    if (this.editNonCdsOrderViewModel.isNew) {
      await this.setupAndCreateNonCdsOrder();
    } else {
      await this.setupAndUpdateNonCdsOrder();
    }
    this.editNonCdsOrderViewModel.saving = false;
  }

  async setupAndCreateNonCdsOrder() {
    const request: CreateNonCdsOrderRequest =
      this.editNonCdsOrderViewModel.toCreateRequest();
    request.facilityCode = this.currentTruck.facilityCode;

    try {
      const response = await orderModule.createNonCdsOrder(request);
      this.editNonCdsOrderViewModel.id = response.id;

      const addToTruckResponse = await truckModule.addNonCdsOrderToTruck({
        truckId: this.currentTruck.id,
        nonCdsOrderId: this.editNonCdsOrderViewModel.id,
      });

      notifications.success(
        "Order Created",
        `Non CDS Order #${response.id} successfully created and added to truck ${this.currentTruck.name}.`
      );
    } catch (ex) {
      notifications.error("Order Not Created", "Failed to create order", ex);
    }
  }

  async setupAndUpdateNonCdsOrder() {
    const request: UpdateNonCdsOrderRequest =
      this.editNonCdsOrderViewModel.toUpdateRequest();

    request.facilityCode = this.currentTruck.facilityCode;

    try {
      const response = await orderModule.updateNonCdsOrder(request);
      notifications.success(
        "Order Updated",
        `Non CDS Order #${request.id} successfully updated.`
      );
    } catch (ex) {
      notifications.error("Update Failed", "Failed to update order", ex);
    }
  }

  async saveCommodity(commodity: NonCdsOrderCommodityViewModel) {
    try {
      if (commodity.isNew) {
        const request = commodity.toCreateRequest();
        request.nonCDSOrderId = this.editNonCdsOrderViewModel.id;
        const response = await orderModule.addNonCdsOrderCommodity(request);
        commodity.id = response.commodityId;
      } else {
        const request = commodity.toUpdateRequest();
        request.nonCDSOrderId = this.editNonCdsOrderViewModel.id;
        const response = await orderModule.updateNonCdsOrderCommodity(request);
      }
      notifications.success("Commodity Save", "Commodity saved");
    } catch (ex) {
      notifications.error("Commodity Save", "Failed to save commodity", ex);
    }
  }

  async removeCommodity(
    commodity: NonCdsOrderCommodityViewModel,
    index: number
  ) {
    if (commodity.isSaved) {
      try {
        await orderModule.removeNonCdsOrderCommodity({
          orderId: this.editNonCdsOrderViewModel.id,
          commodityId: commodity.id,
        });
      } catch (ex) {
        notifications.error(
          "Remove Commodity",
          "Failed to remove commodity",
          ex
        );
        return;
      }
    }

    this.editNonCdsOrderViewModel.commodities.splice(index, 1);
    notifications.success(
      "Remove Commodity",
      "Commodity successfully removed."
    );
  }

  getBooleanOptions() {
    return [
      { label: "Yes", value: "true" },
      { label: "No", value: "false" },
    ];
  }

  get isAgentUser() : boolean {
    return systemModule.deliveryFors.some(x => x.agentId != 0)
  }
}
